import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { setAuth0AccessToken } from 'api/client-side-slices/AuthTokenSlice';
import { PRIME_KYC_LOGO } from 'utils/icons';

type Props = {
  children: React.ReactNode;
};

export const AuthTokenProvider = ({ children }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { isAuthenticated, getIdTokenClaims, isLoading } = useAuth0();
  const [isTokenResolved, setIsTokenResolved] = useState(false);

  useEffect(() => {
    const getToken = async () => {
      try {
        const idTokenClaims = await getIdTokenClaims();
        if (idTokenClaims?.__raw) {
          dispatch(setAuth0AccessToken(idTokenClaims.__raw));
        } else {
          dispatch(setAuth0AccessToken(null));
        }
      } catch (err) {
        console.error('Failed to get Auth0 token:', err);
        dispatch(setAuth0AccessToken(null));
      } finally {
        setIsTokenResolved(true);
      }
    };

    if (isAuthenticated) {
      getToken();
    }
  }, [isAuthenticated, getIdTokenClaims, dispatch]);

  // Hold the app until the token is in the store. Without this, pages that
  // fetch on mount (a refresh straight onto entity/individual details) fire
  // their request before the Authorization header exists and get a 401 that
  // is never retried.
  if (isLoading || (isAuthenticated && !isTokenResolved)) {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <div className="p-2 rounded bg-navy-900 animate-bounce">
          <img src={PRIME_KYC_LOGO} alt="KYC Logo" width="184" height="22" />
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
